/**
 * One predecessor tie in a workbench row — DepChip (type/lag popover, buffered
 * commit) for editable committed edges; a static pill for read-only mode and
 * batch-staged adds (their savepoint edge ids don't survive a preview).
 */
import React from "react";
import type { ExternalCandidate, TaktColorEntry, WorkbenchPred } from "../../../../../../models";
import { autoFontColor } from "../../../../../../utils/color";
import type { PredType } from "../../../../../../utils/p6";
import type { WorkbenchRowLike } from "../../CascadeWorkbenchModal";
import DepChip from "../../../../../pull-plan/depCore/DepChip/DepChip";

interface Props {
  row: WorkbenchRowLike;
  pred: WorkbenchPred;
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  zoneMap: Map<number, string>;
  readOnly: boolean;
  edited: boolean;
  onEdgeChange: (row: WorkbenchRowLike, pred: WorkbenchPred, patch: { type?: PredType; lagDays?: number }) => void;
  onEdgeRemove: (row: WorkbenchRowLike, pred: WorkbenchPred) => void;
  /** Re-point this tie at an activity picked from the schedule search. */
  onEdgeReplace?: (row: WorkbenchRowLike, pred: WorkbenchPred, external: ExternalCandidate) => void;
  /** Server-driven schedule activity search for the typeahead. */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
}

const PredChip: React.FC<Props> = ({
  row,
  pred,
  colors,
  activityWorkCodeMap,
  zoneMap,
  readOnly,
  edited,
  onEdgeChange,
  onEdgeRemove,
  onEdgeReplace,
  fetchExternals,
}) => {
  // Predecessors aren't in the row's section map, so fall back to the pred's
  // OWN work code (and its color) — otherwise every chip renders colourless.
  const wc = activityWorkCodeMap.get(pred.pred_activity_id) || pred.pred_task_code || "";
  const fill = (wc && colors[wc]?.fill) || "";
  const editable = !readOnly && pred.edge_id != null;

  if (!editable) {
    return (
      <span className="cwb-pred">
        {pred.in_section != null && (
          <span
            className={
              "cwb-chip-scope" +
              (pred.in_section ? " cwb-chip-scope--in" : " cwb-chip-scope--out")
            }
          >
            {pred.in_section ? "in plan" : "outside"}
          </span>
        )}
        <span
          className={`cwb-static-chip${pred.is_new ? " is-new" : ""}${fill ? "" : " cwb-static-chip--untagged"}`}
          style={fill ? { background: fill, color: autoFontColor(fill) } : undefined}
          title={`${pred.pred_task_code} · ${pred.pred_task_name}`}
        >
          {pred.driving && <span className="cwb-chip-driving">▸</span>}
          {wc && <b>{wc}</b>} {pred.pred_task_name}
          {pred.is_new && <span className="cwb-chip-new-tag">new</span>}
        </span>
      </span>
    );
  }

  return (
    <span className={edited ? "cwb-chip-edited" : undefined}>
      <DepChip
        workCode={wc || pred.pred_task_code}
        workName={pred.pred_task_name}
        fullName={pred.pred_task_name}
        taskCode={pred.pred_task_code}
        zoneLabel={zoneMap.get(pred.pred_activity_id) || null}
        color={fill}
        predType={pred.pred_type as PredType}
        lagHours={pred.lag_hours}
        driving={pred.driving}
        onChangeRel={(type, lagDays) => onEdgeChange(row, pred, { type, lagDays })}
        onDelete={() => onEdgeRemove(row, pred)}
        fetchExternals={fetchExternals}
        onPickExternal={
          onEdgeReplace ? (ext) => onEdgeReplace(row, pred, ext) : undefined
        }
      />
    </span>
  );
};

export default PredChip;
